/**
 * ErrorCard — Shared error display for self-serve screens.
 * Shows an error message with an optional retry button and fallback link.
 */
import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AlertTriangle, RefreshCw, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function ErrorCard({
  title = 'Something went wrong',
  message = 'We couldn\'t load this page. Please try again.',
  onRetry,
  fallbackPath,
  fallbackLabel = 'Go back',
  className = '',
}) {
  const navigate = useNavigate();

  return (
    <Card className={`max-w-md mx-auto mt-12 ${className}`}>
      <CardContent className="py-8 flex flex-col items-center text-center gap-4">
        <div className="w-12 h-12 rounded-full bg-red-50/50 flex items-center justify-center">
          <AlertTriangle className="h-6 w-6 text-red-400" />
        </div>

        <div>
          <h3 className="text-base font-semibold mb-1">{title}</h3>
          <p className="text-sm text-muted-foreground leading-relaxed">{message}</p>
        </div>

        <div className="flex gap-2">
          {onRetry && (
            <Button onClick={onRetry} className="bg-coral hover:bg-coral/90 text-white">
              <RefreshCw className="h-4 w-4 mr-1.5" />
              Try Again
            </Button>
          )}
          {fallbackPath && (
            <Button variant="outline" onClick={() => navigate(fallbackPath)}>
              <ArrowLeft className="h-4 w-4 mr-1.5" />
              {fallbackLabel}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
